import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

const propTypes = {
  weight: PropTypes.string,
  color: PropTypes.string,
  collapseWhenEmpty: PropTypes.bool,
};

/**
 * SNDisclaimer component.
 *
 * The change and cancellation rules the SN family shows with its exchange form. Extracted so the
 * copy has one home: the form renders it inline on the current layout, and the redesigned shell
 * renders the same block in its footer, below the form.
 *
 * Which rules apply is the tenant's answer, not this component's: a tenant that only cancels is
 * told about cancellations, one that only exchanges about exchanges, and one that does both about
 * both — the same three flags the form's own title follows.
 *
 * @param {object} props - Component props.
 * @param {string} [props.weight] - Font weight, so each placement can match its surroundings.
 * @param {string} [props.color] - Text colour, likewise.
 * @param {boolean} [props.collapseWhenEmpty] - Whether a tenant with no rules renders nothing at
 *   all. The redesign's footer wants that; the form's own row does not, where the empty block is
 *   the left half of a `space-between` and removing it would move the submit button.
 * @returns {JSX.Element|null}
 */
const SNDisclaimer = ({ weight = 'bold', color = undefined, collapseWhenEmpty = true }) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();

  const canExchange = Boolean(features.SHOW_EXCHANGE_TICKET_BUTTON);
  const canCancel = Boolean(features.SHOW_CANCEL_TICKET_BUTTON);

  /**
   * The two leading rules, in the wording that matches what this tenant offers.
   * @returns {{first: string, second: string}}
   */
  const getFeatureCopy = () => {
    if (!canExchange && canCancel) {
      return {
        first: t('disclaimer.exchange_cancelations.first_cancelations'),
        second: t('disclaimer.exchange_cancelations.second_cancelations'),
      };
    }
    if (canExchange && !canCancel) {
      return {
        first: t('disclaimer.exchange_cancelations.first_exchange'),
        second: t('disclaimer.exchange_cancelations.second_exchange'),
      };
    }
    return {
      first: t('disclaimer.exchange_cancelations.first'),
      second: t('disclaimer.exchange_cancelations.second'),
    };
  };

  const featureCopy = getFeatureCopy();
  const showLeadingRules = Boolean(features.SHOW_EXCHANGE_DISCLAIMER);
  const showRefundPending = Boolean(features.SHOW_REFUND_PENDING_DISCLAIMER);

  // A tenant with all three switches off has no rules to show, and an empty block would still
  // draw the footer's mark beside nothing. Inside the form it keeps its place regardless: it is
  // the first child of a `space-between` row, and dropping it would push the submit to the left.
  if (collapseWhenEmpty && !showLeadingRules && !canExchange && !showRefundPending) return null;

  return (
    <Spacing size="XS" vertical>
      {showLeadingRules && (
        <>
          <Text weight={weight} color={color}>
            {featureCopy.first}{' '}
          </Text>
          <Text weight={weight} color={color}>
            {featureCopy.second}
          </Text>
        </>
      )}
      {canExchange && (
        <Text weight={weight} color={color}>
          {' '}
          {t('disclaimer.exchange_cancelations.third')}{' '}
        </Text>
      )}
      {showRefundPending && (
        <Text weight={weight} color={color}>
          {t('disclaimer.exchange_cancelations.refund_pending')}
        </Text>
      )}
    </Spacing>
  );
};

SNDisclaimer.propTypes = propTypes;

export default SNDisclaimer;
